"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserPlus } from "lucide-react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";

import FormInput from "@/app/_module/components/FormInput";
import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/app/_module/components/ui/dialog";
import { Form, FormField } from "@/app/_module/components/ui/form";

import type { CreateLeadSchema } from "../schema/lead-schema";
import { createLeadSchema } from "../schema/lead-schema";

interface CreateLeadModalProps {
  onCreate?: (lead: CreateLeadSchema) => void;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}

const CreateLeadModal = function ({ onCreate, externalOpen, onExternalOpenChange }: CreateLeadModalProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Sync with external open state
  useEffect(() => {
    if (externalOpen !== undefined) {
      setIsOpen(externalOpen);
    }
  }, [externalOpen]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onExternalOpenChange?.(open);
  };

  const form = useForm<CreateLeadSchema>({
    resolver: zodResolver(createLeadSchema),
    defaultValues: {
      name: "",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = form;

  const onSubmit = (values: CreateLeadSchema) => {
    if (onCreate) {
      onCreate(values);
    }
    reset();
    handleOpenChange(false);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      <DialogTrigger asChild>
        <Button
          className="transition-all duration-200 hover:scale-105"
          size="sm"
          variant="outline"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Lead
        </Button>
      </DialogTrigger>
      <ResponsiveDialogContent size="md">
        <DialogHeader>
          <DialogTitle>Create New Lead</DialogTitle>
          <DialogDescription>
            Enter the lead&apos;s name to get started. You can add more details
            later.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit(onSubmit)(e);
            }}
          >
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormInput
                  required
                  error={errors.name}
                  id="name"
                  label="Lead Name"
                  placeholder="Enter lead's full name"
                  {...field}
                />
              )}
            />
            <DialogFooter>
              <Button disabled={!isValid} type="submit">
                Create Lead
              </Button>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
            </DialogFooter>
          </form>
        </Form>
      </ResponsiveDialogContent>
    </Dialog>
  );
};

export default CreateLeadModal;
